import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sparkles, ChevronDown } from "lucide-react";
import { HYPOTHESIS_TYPE_LABELS } from "@/utils/ab-test-utils";
import type { HypothesisVote } from "@/types/ab-tests";

interface SignalsSectionProps {
  isCompleted: boolean;
  winningHypotheses: HypothesisVote[];
}

export function SignalsSection({ isCompleted, winningHypotheses }: SignalsSectionProps) {
  return (
    <div className="space-y-2.5">
      <h3 className="text-xl font-semibold text-foreground">Signals generated</h3>
      {!isCompleted ? (
        <p className="text-sm text-muted-foreground">
          Signals will appear here once this test completes and the winning variant&apos;s hypotheses are contributed to the signal pool.
        </p>
      ) : winningHypotheses.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This test didn&apos;t contribute hypotheses to the signal pool (inconclusive or no hypothesis matrix recorded).
        </p>
      ) : (
        <Collapsible>
          <div className="border border-border rounded-md bg-card">
            <div className="px-4 py-3 grid grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Hypotheses contributed</p>
                <p className="text-lg font-semibold text-foreground">{winningHypotheses.length}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Matched signals</p>
                <p className="text-lg font-semibold text-foreground">0</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">New candidates</p>
                <p className="text-lg font-semibold text-foreground">{winningHypotheses.length}</p>
              </div>
            </div>

            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group w-full flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-primary border-t border-border hover:bg-muted/50 transition-colors"
              >
                <span className="group-data-[state=open]:hidden">View all hypotheses</span>
                <span className="hidden group-data-[state=open]:inline">Hide hypotheses</span>
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <ul className="divide-y divide-border border-t border-border">
                {winningHypotheses.map((h, idx) => (
                  <li key={`${h.id}-${idx}`} className="flex items-start gap-3 px-4 py-2.5">
                    <Sparkles className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <p className="text-sm text-foreground leading-snug">
                        <span className="text-muted-foreground">{HYPOTHESIS_TYPE_LABELS[h.type]}:</span>{" "}
                        {h.instance}
                      </p>
                      <p className="text-xs text-muted-foreground leading-snug">{h.rationale}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                      <Badge variant="outline" className="font-normal">New</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  );
}
